import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ModalEntreeState, ModalAlertState } from '../../types/redux';
import type { RootState } from '../../app/store';

// État initial pour les modals
const initialModalState = {
    open: false,
    entreeInit: null,
    from: null
};

const initialModalAlertState: ModalAlertState = {
    open: false,
    title: '',
    content: ''
};

// Interface pour le modal périmètre avec support mode lecture seule et périmètres partagés
interface ModalPerimState {
    open: boolean;
    perimId?: string;
    readOnly?: boolean;  // Mode lecture seule pour les périmètres partagés
    isSharedPerimetre?: boolean;  // Indique si c'est un périmètre partagé
    sharedBy?: string;  // Nom de la personne qui a partagé
    sharedOwnerId?: string;  // ID du propriétaire du périmètre partagé
    sharedPermission?: 'read' | 'edit';  // Permission accordée
}

const initialModalPerimState: ModalPerimState = {
    open: false,
    perimId: undefined,
    readOnly: false,
    isSharedPerimetre: false,
    sharedBy: undefined,
    sharedOwnerId: undefined,
    sharedPermission: undefined
};

/**
 * Dialogue « Partager la note » ouvert hors d'une carte (chantier C8, vague 3) :
 * depuis le menu ⋯ de l'éditeur ou depuis l'action « Partager » de la barre
 * d'état après la création d'une citation. Rendu par `ShareNoteDialogHost`.
 */
export interface ShareNoteState {
    open: boolean;
    noteId?: string;
    noteTitle?: string;
}

/** Action optionnelle d'un message de la barre d'état — sérialisable (pas de callback). */
export type StatusBarAction =
    | { label: string; kind: 'share-note'; noteId: string; noteTitle?: string }
    /** Naviguer vers une route du shell courant (toast « Voir » de la capture mobile, C11). */
    | { label: string; kind: 'navigate'; to: string }
    /** Basculer sur le shell bureau (notification à consulter sur le bureau, C11 SP5). */
    | { label: string; kind: 'desktop' };

export interface StatusBarState {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
    action?: StatusBarAction;
}

/**
 * Relais de création d'entrée : ModalEntree y dépose l'entrée qu'il vient
 * d'enregistrer pour que l'écran appelant reprenne son geste dessus. Le
 * DocReader s'en sert pour relier un passage à un mot-clé qui n'existait pas :
 * la clé déposée est celle réellement enregistrée, que l'utilisateur peut avoir
 * corrigée dans le dialogue. `from` reprend l'origine passée à `setModalEntree`.
 */
export interface EntreeHandoff {
    from: string;
    entreeId: string;
    cle: string;
}

/** Portée d'une dictée (C12) : un document ouvert, un dossier, ou rien (tous les documents). */
export interface DictationScope {
    fileId?: string;
    /** Nom du document, pour l'en-tête « Dans « … » ». */
    fileName?: string;
    perimetreId?: string;
}

/**
 * Ouverture d'une dictée : sa portée, plus `autoStart` — le micro démarre dès
 * l'ouverture (appui long sur le bouton « Capturer » du shell mobile : il n'y a
 * plus qu'à dicter). Le geste porte l'intention ; la portée reste `DictationScope`.
 */
export type DictationOpenOptions = DictationScope & { autoStart?: boolean };

// Slice temporaire pour les modals avec ModalEntree
const modalSlice = createSlice({
    name: 'modals',
    initialState: {
        modalEntreeVisible: initialModalState as ModalEntreeState,
        modalAlert: initialModalAlertState,
        modalPerimVisible: initialModalPerimState,
        showFilePanel: false,
        quickCaptureOpen: false,
        /**
         * Texte pré-rempli du champ de capture rapide — alimenté par la cible de
         * partage entrant (`/capture?title=&text=&url=`, spec § 2.4). Vide pour une
         * ouverture ordinaire (bouton, raccourci). Remis à vide à la fermeture.
         */
        quickCaptureInitialText: '',
        /**
         * Dictée « source » (C12) : la sheet (mobile) ou le modal (bureau) « Dicter
         * une citation », et la portée de la recherche — un document (depuis le
         * lecteur), un dossier, ou tous les documents.
         */
        dictationOpen: false,
        dictationScope: {} as DictationScope,
        dictationAutoStart: false,
        shareNote: { open: false } as ShareNoteState,
        entreeHandoff: null as EntreeHandoff | null,
        statusBar: {
            open: false,
            message: '',
            severity: 'info'
        } as StatusBarState
    },
    reducers: {
        setModalEntree: (state, action: PayloadAction<ModalEntreeState>) => {
            state.modalEntreeVisible = action.payload;
        },
        setModalAlert: (state, action: PayloadAction<ModalAlertState>) => {
            state.modalAlert = action.payload;
        },
        setModalPerim: (state, action: PayloadAction<ModalPerimState>) => {
            state.modalPerimVisible = action.payload;
        },
        setShowFilePanel: (state, action: PayloadAction<boolean>) => {
            state.showFilePanel = action.payload;
        },
        openQuickCapture: (state, action: PayloadAction<{ initialText?: string } | undefined>) => {
            state.quickCaptureOpen = true;
            state.quickCaptureInitialText = action.payload?.initialText ?? '';
        },
        closeQuickCapture: (state) => {
            state.quickCaptureOpen = false;
            state.quickCaptureInitialText = '';
        },
        openDictation: (state, action: PayloadAction<DictationOpenOptions | undefined>) => {
            const { autoStart, ...scope } = action.payload ?? {};
            state.dictationOpen = true;
            state.dictationScope = scope;
            state.dictationAutoStart = Boolean(autoStart);
        },
        closeDictation: (state) => {
            state.dictationOpen = false;
            state.dictationScope = {};
            state.dictationAutoStart = false;
        },
        setStatusBar: (state, action: PayloadAction<StatusBarState>) => {
            state.statusBar = action.payload;
        },
        openShareNote: (state, action: PayloadAction<{ noteId: string; noteTitle?: string }>) => {
            state.shareNote = { open: true, noteId: action.payload.noteId, noteTitle: action.payload.noteTitle };
        },
        closeShareNote: (state) => {
            state.shareNote = { open: false };
        },
        setEntreeHandoff: (state, action: PayloadAction<EntreeHandoff>) => {
            state.entreeHandoff = action.payload;
        },
        clearEntreeHandoff: (state) => {
            state.entreeHandoff = null;
        }
    }
});

export const { setModalEntree, setModalAlert, setModalPerim, setShowFilePanel, setStatusBar, openQuickCapture, closeQuickCapture, openDictation, closeDictation, openShareNote, closeShareNote, setEntreeHandoff, clearEntreeHandoff } = modalSlice.actions;
export const selectShareNote = (state: RootState): ShareNoteState => state.modals.shareNote;
export const selectEntreeHandoff = (state: RootState): EntreeHandoff | null => state.modals.entreeHandoff;

// Sélecteurs
export const ModalEntreeData = (state: RootState) => state.modals.modalEntreeVisible;
export const selectModalPerimVisible = (state: RootState) => state.modals.modalPerimVisible;
export const selectShowFilePanel = (state: RootState) => state.modals.showFilePanel;
export const selectQuickCaptureOpen = (state: RootState) => state.modals.quickCaptureOpen;
export const selectQuickCaptureInitialText = (state: RootState): string => state.modals.quickCaptureInitialText;
export const selectDictationOpen = (state: RootState): boolean => state.modals.dictationOpen;
export const selectDictationScope = (state: RootState): DictationScope => state.modals.dictationScope;
export const selectDictationAutoStart = (state: RootState): boolean => state.modals.dictationAutoStart;

export default modalSlice.reducer;
